/**
 * Axios client for spectraiq-backend.
 *
 * SPEC-FE-401 (Sprint 6) — Keycloak-backed bearer auth.
 * Adds:
 *   - Keycloak access-token bearer header on every request (lazily resolved)
 *   - 401 → force one token refresh, retry the request once; if the refresh
 *     token is dead too, dispatch spectraiq:auth:expired and redirect to login
 *   - X-Tenant-ID echo for visibility (backend authoritative)
 */

import axios from 'axios';

const baseURL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8080/api';

// Token getter is set from <SpectraIqAuthProvider /> at mount time using
// Keycloak's updateToken(30)+token pair from keycloak-js.
let tokenGetter = async () => null;

export function setAuthTokenGetter(fn) {
  tokenGetter = fn;
}

// Auth actions injected by the provider: force-refresh the access token
// (returns the fresh token or null) and redirect to the login page.
let authActions = {
  forceRefresh: async () => null,
  login: () => {},
};

export function setAuthActions(actions) {
  authActions = { ...authActions, ...actions };
}

export const api = axios.create({
  baseURL,
  timeout: 30_000,
});

api.interceptors.request.use(async (config) => {
  try {
    const token = await tokenGetter();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch (err) {
    // Pre-login or expired session — request will 401 and surface to the UI.
    console.warn('[api] token getter failed', err);
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const original = err?.config;
    if (err?.response?.status === 401 && original && !original._authRetried) {
      // Token was valid when attached but rejected by the server (expired
      // mid-flight or clock skew). Force one refresh and retry the request.
      original._authRetried = true;
      try {
        const fresh = await authActions.forceRefresh();
        if (fresh) {
          original.headers.Authorization = `Bearer ${fresh}`;
          return api.request(original);
        }
      } catch { /* fall through to login */ }
      // Refresh token is dead too — announce it and bounce to login.
      window.dispatchEvent(new CustomEvent('spectraiq:auth:expired'));
      authActions.login();
    }
    return Promise.reject(err);
  },
);
